/*
    Takes in a strinified array and adds the ability to paginate, filter and sort the data.
*/

/*
  interface GlossaryListConfig {
    key: string
    perPage: number
  }
*/

export default class GlossaryList {
  config = {
    key: undefined,
    perPage: 1,
  };

  items = new Map();
  // GlossaryListConfig
  constructor(config) {
    this.config = config;
    if (!this.config.key) throw new Error("GlossaryList: key is required");

    const items = this.DOMItems;
    for (let i = 0; i < items.length; i++) {
      this.items.set(items[i], {
        index: i,
        match: true,
        order: i + 1,
        value: (items[i].getAttribute(`gl-value`) || "").toLowerCase(),
        popular: items[i].getAttribute(`gl-popular`) === "true" ? true : false,
      });
    }

    this.#filterItems();
    this.#generatePagination();
    this.#formInit();
  }

  // ----------------------------------------
  // Methods
  // //////////
  // Pagination
  #generatePagination() {
    const container = this.DOMPaginationContainer;
    if (!container) return;

    const pages = this.pages;
    const currentPage = this.page;

    // clear
    container.innerHTML = "";

    for (let i = 1; i <= pages; i++) {
      const button = document.createElement("button");
      button.innerText = i;
      button.dataset.page = i;
      button.addEventListener("click", () => this.#changePage(i));

      if (i === currentPage) {
        button.classList.add("active");
      }

      container.appendChild(button);
    }
  }
  #changePage(page) {
    this.#setLocation(page);
    this.#toggleItems();

    const container = this.DOMPaginationContainer;
    const buttons = [...container.querySelectorAll("button")];
    for (let i = 0; i < buttons.length; i++) {
      const button = buttons[i];
      if (parseInt(button.dataset.page) === page) {
        button.classList.add("active");
      } else {
        button.classList.remove("active");
      }
    }

    window.scrollTo({
      top: 300,
      behavior: "smooth",
    });
  }
  // //////////
  // Forms
  #formInit() {
    const form = this.DOMForm;
    if (!form) return;

    this.#checkboxChange();
    this.#setFormValues();

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.#formSubmit();
    });
    form.addEventListener("change", (e) => {
      this.#formSubmit();
    });
  }
  #setFormValues() {
    const form = this.DOMForm;
    const searchParams = new URLSearchParams(window.location.search);

    // set initial char
    const initialChar = searchParams.get("filter[initial-char]");
    if (initialChar) {
      const checkbox = form.querySelector(
        `input[type=checkbox][value=${initialChar}]`
      );
      checkbox.checked = true;
    }

    // set search
    const search = searchParams.get("filter[search]");
    if (search) {
      const input = form.querySelector(`input[type=search]`);
      input.value = search;
    }

    const filterOptions = searchParams.get("filter[option]");
    if (filterOptions) {
      const select = form.querySelector("#gl-filter");
      const option = select.querySelector(`option[value=${filterOptions}]`);
      if (option) option.selected = true;
    }

    // set sort
    const sort = searchParams.get("sort");
    if (sort) {
      const select = form.querySelector(`select`);
      const option = select.querySelector(`option[value=${sort}]`);
      if (option) option.selected = true;
    }
  }
  #checkboxChange() {
    const form = this.DOMForm;
    const checkboxes = [...form.querySelectorAll("input[type=checkbox]")];
    // only one checkbox can be checked at a time
    for (let i = 0; i < checkboxes.length; i++) {
      const checkbox = checkboxes[i];
      checkbox.addEventListener("change", () => {
        if (checkbox.checked) {
          for (let j = 0; j < checkboxes.length; j++) {
            const checkbox2 = checkboxes[j];
            if (checkbox2 !== checkbox) {
              checkbox2.checked = false;
            }
          }
        }
      });
    }
  }
  #formSubmit() {
    const form = this.DOMForm;

    const formData = new FormData(form);

    const initialCharFilter = formData.get("gl-initial-search");
    const search = formData.get("gl-search");
    const filterOptions = formData.get("gl-filter");
    const sort = formData.get("gl-sort");

    const filters = {};
    if (initialCharFilter) {
      filters.initialChar = initialCharFilter;
    }
    if (search) {
      filters.search = search;
    }
    if (filterOptions) {
      filters.filterOptions = filterOptions;
    }

    const action = form.getAttribute("action");

    // if the form has no action, set the location
    if (!action) this.#setLocation(1, filters, sort);
    else {
      form.submit();
    }
  }

  // //////////
  // Items
  // Handles setting item visibility by filter, sorting and pagination
  #filterItems() {
    const searchParams = new URLSearchParams(window.location.search);

    // for items
    this.items.forEach((item) => {
      item.match = false;
      item.order = item.index + 1;

      // filter by search
      const search = searchParams.get("filter[search]");
      if (search) {
        if (item.value.includes(search.toLowerCase())) {
          item.match = true;
        }
      }

      // Filter by initial char
      const initialChar = searchParams.get("filter[initial-char]");
      if (initialChar) {
        // if the item is already visible, don't change it
        if (item.value[0] === initialChar?.toLowerCase()) {
          item.match = true;
        }
      }

      // Filter by popular
      const filterOptions = searchParams.get("filter[option]");
      if (filterOptions) {
        if (filterOptions === "popular") {
          if (item.popular) {
            item.match = true;
          } else {
            item.match = false;
          }
        }
      }

      if (!initialChar && !search && !filterOptions) {
        item.match = true;
      }
    });

    // sort
    const sort = searchParams.get("sort");

    let sortDirection = "asc";
    let sortKey = "title";
    if (sort) {
      sortDirection = sort[0] === "-" ? "desc" : "asc";
      sortKey = sortDirection === "desc" ? sort.slice(1) : sort;
    }

    let sortedItems = [];
    switch (sortKey) {
      case "title":
        sortedItems = [...this.items.entries()].sort((a, b) => {
          const firstChar = a[1].value[0];
          const secondChar = b[1].value[0];

          if (sortDirection === "desc") {
            return firstChar < secondChar ? 1 : -1;
          } else {
            return firstChar > secondChar ? 1 : -1;
          }
        });

        break;
    }

    let index = 0;
    this.items.forEach((item) => {
      item.order = sortedItems.findIndex((i) => i[1].index === item.index) + 1;
      index++;
    });

    this.#toggleItems();
    this.#generatePagination();
  }
  #toggleItems() {
    // get all matches, splut into pages
    const matches = this.matches;
    const pages = [];
    for (let i = 0; i < matches.length; i += this.config.perPage) {
      pages.push(matches.slice(i, i + this.config.perPage));
    }

    for (let [key, value] of this.items) {
      key.style.display = "none";
      key.style.order = value.order;
    }

    // if no matches, show no data
    if (this.DOMNoData) {
      if (matches.length === 0) {
        this.DOMNoData.style.display = "flex";
      } else {
        this.DOMNoData.style.display = "none";
      }
    }

    // show items on current page
    const currentPage = this.page;
    const currentPageItems = pages[currentPage - 1];
    currentPageItems?.forEach((item) => {
      item[0].style.display = "flex";
    });
  }
  #setLocation(page, filters, sort, applyFilters = true) {
    const searchParams = new URLSearchParams(window.location.search);

    // set page
    if (page !== undefined) {
      searchParams.set("page", page);
    } else {
      searchParams.delete("page");
    }

    // set filters
    if (filters) {
      if (filters?.initialChar !== undefined) {
        searchParams.set("filter[initial-char]", filters.initialChar);
      } else {
        searchParams.delete("filter[initial-char]");
      }
      if (filters?.search !== undefined) {
        searchParams.set("filter[search]", filters.search);
      } else {
        searchParams.delete("filter[search]");
      }
      if (filters?.filterOptions !== undefined) {
        searchParams.set("filter[option]", filters.filterOptions);
      } else {
        searchParams.delete("filter[option]");
      }
    }

    // set sort
    if (sort) {
      searchParams.set("sort", sort);
    } else {
      // searchParams.delete("sort");
    }

    window.history.pushState(
      {},
      "",
      `${window.location.pathname}?${searchParams.toString()}`
    );

    if (applyFilters) this.#filterItems();
  }

  // ----------------------------------------
  // Getters
  get page() {
    const searchParams = new URLSearchParams(window.location.search);
    const page = searchParams.get("page");
    return page ? parseInt(page) : 1;
  }
  get pages() {
    // filter items by visible only
    const pages = Math.ceil(this.matches.length / this.config.perPage);
    return pages;
  }
  get matches() {
    let items = [...this.items.entries()].filter((item) => item[1].match);
    items.sort((a, b) => {
      return a[1].order - b[1].order;
    });
    return items;
  }
  get DOMItems() {
    return [...document.querySelectorAll(`[gl-item="${this.config.key}"]`)];
  }
  get DOMPaginationContainer() {
    return document.querySelector(`[gl-pagination="${this.config.key}"]`);
  }
  get DOMForm() {
    return document.querySelector(`[gl-form="${this.config.key}"]`);
  }
  get DOMNoData() {
    return document.querySelector(`[gl-no-data="${this.config.key}"]`);
  }
  get DOMContainer() {
    return document.querySelector(`[gl-container="${this.config.key}"]`);
  }

  // ----------------------------------------
  // Functions
}
